import { JMCLink } from "@components/JMCLink/JMCLink";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { Banner } from "@jmc/solid-design-system/src/components/atoms/Banner/Banner";
import { SnackBar } from "@jmc/solid-design-system/src/components/atoms/SnackBar/SnackBar";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { useLocation } from "@reach/router";
import { ApplicationState } from "@redux/modules";
import { load as loadPrivacyPolicyUpdate, PrivacyPolicy } from "@redux/modules/privacyPolicyUpdate";
import dayjs from "dayjs";
import { graphql, useStaticQuery } from "gatsby";
import isEqual from "lodash/isEqual";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import regexifyString from "regexify-string";
import { useLocalStorage } from "usehooks-ts";

interface PrivacyPolicyProps {
    currentPage?: string;
}

interface LegalDocumentNode {
    onetrust_privacy_policy_id: string;
    publish_details: {
        locale: string;
    };
}

const ShowPrivacyPolicyUpdate = (props: PrivacyPolicyProps): JSX.Element => {
    const [alreadySeenPrivacyPolicy, setAlreadySeenPrivacyPolicy] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const lastPrivacyPolicyUpdate = useSelector(
        (state) => (state as ApplicationState).privacyPolicy.data as unknown as PrivacyPolicy,
        isEqual,
    );
    const [lastVisted, setLastVisited] = useLocalStorage("lastVisited", dayjs("1990-01-01T12:00:00.000Z"));
    const dispatch = useDispatch();
    const locale = useLocale();
    const url = useLocation();
    const [alt, setAlt] = useState(url.href);
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();
    const { allContentstackLegalDocuments } = useStaticQuery(graphql`
        query {
            allContentstackLegalDocuments {
                nodes {
                    onetrust_privacy_policy_id
                    publish_details {
                        locale
                    }
                }
            }
        }
    `);
    const policyId = allContentstackLegalDocuments.nodes.find(
        (node: LegalDocumentNode) => node.onetrust_privacy_policy_id && node?.publish_details?.locale === locale,
    );

    //if notification is closed, then update the last visited
    const handleClose = (): void => {
        setShowNotification(false);
        setLastVisited(dayjs());
    };
    //getting the policy latest update date
    useEffect(() => {
        if (policyId) {
            dispatch(loadPrivacyPolicyUpdate({ policyId: policyId.onetrust_privacy_policy_id }));
        }
    }, []);

    //checking if one trust privacy policy has been interacted with at least once
    useEffect(() => {
        if (typeof document !== "undefined") {
            const cookies = document.cookie;
            setAlreadySeenPrivacyPolicy(cookies.includes("OptanonAlertBoxClosed"));
        }
    }, []);

    useEffect(() => {
        //check if user navigated, if so update the last visited value
        if (url.href !== alt) {
            setLastVisited(dayjs());
            setShowNotification(false);
            setAlt(url.href);
        } else if (
            //check if the notification needs to be displayed
            alreadySeenPrivacyPolicy &&
            lastPrivacyPolicyUpdate?.lastPublishedDate &&
            dayjs(lastVisted).isBefore(dayjs(lastPrivacyPolicyUpdate?.lastPublishedDate))
        ) {
            setShowNotification(true);
        }
    }, [lastPrivacyPolicyUpdate, lastVisted, alreadySeenPrivacyPolicy, url]);

    // "We've updated our [privacy policy](url-to-privacy-policy). You can read it [here](url-to-privacy-policy). By continuing to browse our platform, you agree to the update."
    const snackBarText = (): (string | JSX.Element)[] => {
        const PrivacyPolicyPlaceholder = "[Privacy Policy]";
        const Hereholder = "[here]";
        const input = t(
            "common:We've updated our {{PRIVACY_POLICY}}. You can read it {{HERE}}. By continuing to browse our platform, you agree to the updates.",
            {
                PRIVACY_POLICY: PrivacyPolicyPlaceholder,
                HERE: Hereholder,
            },
        );

        const decorateLink = (match: string): JSX.Element | string => {
            return (
                <JMCLink
                    isAllowedOnMedical
                    key={`OneTrust-Privacy-Policy-Link-${match}`}
                    external={false}
                    commercial={true}
                    url={"/services/privacy-policy"}
                    name={"Privacy Policy"}
                    placement={props?.currentPage + jnjFullBranded ? ":Banner" : ":Snackbar"}
                >
                    <Typography variant="span">
                        {match === PrivacyPolicyPlaceholder
                            ? t("Privacy Policy", { ns: "common" })
                            : Hereholder
                              ? t("here", { ns: "common" })
                              : ""}
                    </Typography>
                </JMCLink>
            );
        };
        return regexifyString({
            pattern: /\[.*?\]/gim,
            decorator: decorateLink,
            input,
        });
    };
    if (showNotification) {
        return jnjFullBranded ? (
            <Banner
                id="PrivacyPolicyUpdate"
                type="warning"
                showIcon={true}
                dismissText={t("Close", { ns: "common" })}
                onDismiss={handleClose}
                text={<>{snackBarText()}</>}
            ></Banner>
        ) : (
            <SnackBar color="warning" onClose={handleClose} margin={true} contentSpacingWidth={true}>
                <SnackBar.Text text={<>{snackBarText()}</>} />
            </SnackBar>
        );
    }

    return null;
};

export default ShowPrivacyPolicyUpdate;
